import { ComponentPropsWithoutRef } from "react";
import { Tabs as PrimitiveTabs } from "radix-ui";
import classNames from "classnames";
import styles from "./Tabs.module.scss";

type TabsProps = ComponentPropsWithoutRef<typeof PrimitiveTabs.Root>;

const Tabs = ({ className, orientation = "horizontal", children, ...props }: TabsProps) => {
	return (
		<PrimitiveTabs.Root
			{...props}
			className={classNames(styles.tabs, className)}
			data-orientation={orientation}
		>
			{children}
		</PrimitiveTabs.Root>
	);
};

type TabsListProps = ComponentPropsWithoutRef<typeof PrimitiveTabs.List> & {
	fullWidth?: boolean;
};

const TabsList = ({ className, children, fullWidth = false, ...props }: TabsListProps) => {
	return (
		<PrimitiveTabs.List
			className={classNames(
				styles["tabs__list"],
				{ [styles["tabs__list--full-width"]]: fullWidth },
				className
			)}
			{...props}
		>
			{children}
		</PrimitiveTabs.List>
	);
};

type TabsTriggerProps = ComponentPropsWithoutRef<typeof PrimitiveTabs.Trigger> & {
	colorTextWhenActive?: boolean;
	activeColor?: string;
};

const TabsTrigger = ({
	className,
	children,
	value,
	colorTextWhenActive = false,
	activeColor = "var(--button-primary-background-color, $button-primary-background-color)",
	...props
}: TabsTriggerProps) => {
	return (
		<PrimitiveTabs.Trigger
			style={{ "--active-color": activeColor } as React.CSSProperties}
			className={classNames(
				styles["tabs__trigger"],
				{ [styles["tabs__trigger--with-active-color"]]: colorTextWhenActive },
				className
			)}
			value={value}
			{...props}
		>
			{children}
		</PrimitiveTabs.Trigger>
	);
};

type TabsContentProps = ComponentPropsWithoutRef<typeof PrimitiveTabs.Content>;

const TabsContent = ({ className, children, value, ...props }: TabsContentProps) => {
	return (
		<PrimitiveTabs.Content
			className={classNames(styles["tabs__content"], className)}
			value={value}
			{...props}
		>
			{children}
		</PrimitiveTabs.Content>
	);
};

export { Tabs, TabsList, TabsTrigger, TabsContent };
